import React, { useState, useEffect } from 'react';
import { Plus, Search, TrendingUp, AlertCircle, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import { tradingService, getErrorMessage } from '../services/api';

const UserStockAddPage = () => {
  // 종목 검색 상태 (TradingPage에서 복사)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // 기존 상태
  const [userNote, setUserNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentStocks, setRecentStocks] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  // 종목 검색 함수 (TradingPage에서 복사 및 수정)
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('검색어를 입력해주세요.');
      return;
    }
    
    setIsSearching(true);
    setSearchResults([]);
    setSelectedStock(null);
    
    try {
      console.log('🔍 종목 검색 시작:', searchQuery);
      const response = await tradingService.searchStock(searchQuery);
      console.log('🔍 검색 응답 전체:', response);
      
      if (response && response.success) {
        const results = response.results || [];
        console.log('🔍 검색 결과:', results);
        
        setSearchResults(results);
        
        if (results.length > 0) {
          toast.success(`${results.length}개 종목을 찾았습니다.`);
        } else {
          toast('검색된 종목이 없습니다.');
        }
      } else {
        console.error('검색 응답 구조 이상:', response);
        toast.error(response?.error || '종목 검색에 실패했습니다.');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('검색 오류:', error);
      const errorMsg = getErrorMessage(error) || '종목 검색에 실패했습니다.';
      toast.error(errorMsg);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // 종목 선택 핸들러
  const handleSelectStock = (stock) => {
    console.log('📈 종목 선택:', stock);
    setSelectedStock(stock);
    toast.success(`${stock.stock_name} 선택됨`);
  };

  // 최근 추가한 사용자 종목 조회
  const fetchRecentUserStocks = async () => {
    setLoadingRecent(true);
    try {
      const response = await fetch('/recommendation-api/user-stocks/recent');
      if (response.ok) {
        const data = await response.json();
        setRecentStocks(data.user_stocks || []);
      } else {
        console.error('최근 종목 조회 실패:', response.status);
      }
    } catch (error) {
      console.error('최근 종목 조회 오류:', error);
    } finally {
      setLoadingRecent(false);
    }
  };

  // 컴포넌트 마운트시 최근 종목 조회
  useEffect(() => {
    fetchRecentUserStocks();
  }, []);

  // 사용자 종목 추가 제출
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStock) {
      toast.error('종목을 검색하고 선택해주세요.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('/recommendation-api/user-stocks/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: selectedStock.stock_name,
          stock_code: selectedStock.stock_code,  // ✅ stock_code 추가!
          user_note: userNote.trim()
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.status === 'analysis_started') {
          toast.success(`✅ ${data.message}`);
          setSearchQuery('');
          setSearchResults([]);
          setSelectedStock(null);
          setUserNote('');
          // 최근 목록 새로고침
          setTimeout(() => {
            fetchRecentUserStocks();
          }, 1000);
        } else if (data.status === 'already_exists') {
          toast.error(`⚠️ ${data.message}`);
        }
      } else {
        toast.error(`❌ ${data.detail || '종목 추가 실패'}`);
      }
    } catch (error) {
      console.error('종목 추가 오류:', error);
      toast.error('❌ 네트워크 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Plus className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">사용자 종목 추가</h1>
          </div>
          <p className="text-gray-600">
            관심 있는 종목을 직접 추가하면 AI가 분석해서 추천 목록에 포함시킵니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* 왼쪽: 종목 추가 폼 */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* 종목 검색 (TradingPage 스타일) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    종목 검색
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="종목명 또는 종목코드 입력 (예: 삼성전자, 005930, SK하이닉스, 000660)"
                      disabled={isSubmitting || isSearching}
                    />
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={isSearching}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {isSearching ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          검색중...
                        </>
                      ) : (
                        <>
                          <Search className="h-5 w-5" />
                          검색
                        </>
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    종목명 또는 코드를 입력하세요
                  </p>
                  <p className="text-xs text-gray-400">
                    예: 삼성전자, 005930, SK하이닉스, 000660
                  </p>
                </div>

                {/* 검색 결과 표시 */}
                {searchResults.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-700">
                        검색 결과 ({searchResults.length}개)
                      </p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {searchResults.map((stock, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelectStock(stock)}
                          className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                            selectedStock?.stock_code === stock.stock_code ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{stock.stock_name}</span>
                                {selectedStock?.stock_code === stock.stock_code && (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm text-gray-600">{stock.stock_code}</span>
                                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                                  {stock.market_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 선택된 종목 표시 */}
                {selectedStock && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-medium text-green-900">선택된 종목</span>
                        </div>
                        <p className="text-sm text-green-800">
                          <span className="font-medium">{selectedStock.stock_name}</span>
                          <span className="mx-2">|</span>
                          <span>{selectedStock.stock_code}</span>
                          <span className="mx-2">|</span>
                          <span className="text-xs">{selectedStock.market_name}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 메모 입력 */}
                <div>
                  <label htmlFor="userNote" className="block text-sm font-medium text-gray-700 mb-2">
                    분석 메모 (선택사항)
                  </label>
                  <textarea
                    id="userNote"
                    value={userNote}
                    onChange={(e) => setUserNote(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="예: AI 반도체 관련 투자 검토, 전기차 배터리 산업 전망"
                    disabled={isSubmitting}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    관심 이유나 분석 관점을 적어주시면 AI 분석에 참고됩니다.
                  </p>
                </div>

                {/* 제출 버튼 */}
                <button
                  type="submit"
                  disabled={isSubmitting || !selectedStock}
                  className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-medium transition-all ${
                    isSubmitting || !selectedStock
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white transform hover:scale-105'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      AI 분석 요청 중...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-5 w-5" />
                      AI 분석 요청
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* 안내 사항 */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-1">분석 소요 시간</p>
                  <p className="text-blue-700">
                    AI 분석은 5-10분 정도 소요되며, 완료 후 추천 목록에서 확인할 수 있습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 오른쪽: 최근 추가한 종목 목록 */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">최근 추가한 종목</h2>
              <button
                onClick={fetchRecentUserStocks}
                disabled={loadingRecent}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
              >
                <RefreshCw className={`h-4 w-4 ${loadingRecent ? 'animate-spin' : ''}`} />
                {loadingRecent ? '새로고침 중...' : '새로고침'}
              </button>
            </div>

            {loadingRecent ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : recentStocks.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500">아직 추가한 종목이 없습니다.</p>
                <p className="text-sm text-gray-400 mt-1">
                  첫 번째 관심 종목을 추가해보세요!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentStocks.map((stock, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900">{stock.company_name}</h3>
                          {stock.stock_code && (
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                              {stock.stock_code}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {stock.summary || '분석 중...'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(stock.analysis_date).toLocaleString('ko-KR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {stock.attention_score > 0 ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span className="text-sm font-medium text-green-600">
                              {stock.attention_score}점
                            </span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-5 w-5 text-yellow-500" />
                            <span className="text-sm text-yellow-600">분석중</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 하단 가이드 */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">종목 추가 가이드</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">1. 종목 검색</h4>
              <p className="text-sm text-gray-600">
                회사명을 입력하면 실시간으로 정확한 종목을 검색합니다.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">2. AI 분석</h4>
              <p className="text-sm text-gray-600">
                AI가 해당 기업의 투자 가치를 종합적으로 분석합니다.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-6 w-6 text-purple-600" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">3. 추천 반영</h4>
              <p className="text-sm text-gray-600">
                분석 완료 후 추천 포트폴리오에 자동 포함됩니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserStockAddPage;
